import { useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import { products, fontSans, hexToRgba } from './theme'

interface Node {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  color: string
  pulsePhase: number
  type: 'agent' | 'data' | 'insight'
}

interface Connection {
  from: number
  to: number
  progress: number
  speed: number
  active: boolean
}

export function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animFrame = 0
    let width = 0
    let height = 0

    const resize = () => {
      width = canvas.width = canvas.offsetWidth
      height = canvas.height = canvas.offsetHeight
    }
    resize()
    window.addEventListener('resize', resize)

    const nodeCount = 28
    const nodes: Node[] = []
    const connections: Connection[] = []

    const nodeColors = ['#4A90E2', '#9D4EDD', '#2DD4BF', '#b794f4']

    for (let i = 0; i < nodeCount; i++) {
      const type: Node['type'] = i < 8 ? 'data' : i < 20 ? 'agent' : 'insight'
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius:
          type === 'agent' ? 5 + Math.random() * 4 : 3 + Math.random() * 2,
        color: nodeColors[Math.floor(Math.random() * nodeColors.length)],
        pulsePhase: Math.random() * Math.PI * 2,
        type,
      })
    }

    for (let i = 0; i < nodeCount; i++) {
      for (let j = i + 1; j < nodeCount; j++) {
        if (Math.random() < 0.15) {
          connections.push({
            from: i,
            to: j,
            progress: Math.random(),
            speed: 0.002 + Math.random() * 0.004,
            active: Math.random() > 0.3,
          })
        }
      }
    }

    let time = 0
    let frame = 0

    const draw = () => {
      // Skip every other frame — the drift is slow, ~30fps is visually
      // identical and halves the painting cost on weak devices.
      frame++
      if (frame % 2 === 0) {
        animFrame = requestAnimationFrame(draw)
        return
      }
      ctx.clearRect(0, 0, width, height)
      time += 0.032

      // Move nodes
      nodes.forEach((n) => {
        n.x += n.vx
        n.y += n.vy
        if (n.x < 0 || n.x > width) n.vx *= -1
        if (n.y < 0 || n.y > height) n.vy *= -1
        n.x = Math.max(0, Math.min(width, n.x))
        n.y = Math.max(0, Math.min(height, n.y))
      })

      // Draw connections
      connections.forEach((conn) => {
        if (!conn.active) return
        const a = nodes[conn.from]
        const b = nodes[conn.to]
        const dx = b.x - a.x
        const dy = b.y - a.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist > 280) return

        const opacity = Math.max(0, 0.25 - dist / 1400)

        // Static line
        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        ctx.lineTo(b.x, b.y)
        ctx.strokeStyle = `rgba(74, 144, 226, ${opacity})`
        ctx.lineWidth = 0.5
        ctx.stroke()

        // Travelling packet
        conn.progress += conn.speed
        if (conn.progress > 1) conn.progress = 0
        const px = a.x + dx * conn.progress
        const py = a.y + dy * conn.progress

        const packetGrad = ctx.createRadialGradient(px, py, 0, px, py, 6)
        packetGrad.addColorStop(
          0,
          a.type === 'agent'
            ? 'rgba(157, 78, 221, 0.9)'
            : 'rgba(74, 144, 226, 0.9)',
        )
        packetGrad.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.beginPath()
        ctx.arc(px, py, 6, 0, Math.PI * 2)
        ctx.fillStyle = packetGrad
        ctx.fill()
      })

      // Draw nodes
      nodes.forEach((n) => {
        const pulse = Math.sin(time * 2 + n.pulsePhase) * 0.5 + 0.5

        if (n.type === 'agent') {
          // Outer glow ring for agents
          const glowR = n.radius * (2.5 + pulse * 1.5)
          const glowGrad = ctx.createRadialGradient(
            n.x,
            n.y,
            0,
            n.x,
            n.y,
            glowR,
          )
          glowGrad.addColorStop(0, `rgba(157, 78, 221, ${0.25 + pulse * 0.15})`)
          glowGrad.addColorStop(1, 'rgba(0,0,0,0)')
          ctx.beginPath()
          ctx.arc(n.x, n.y, glowR, 0, Math.PI * 2)
          ctx.fillStyle = glowGrad
          ctx.fill()
        }

        // Core
        const coreGrad = ctx.createRadialGradient(
          n.x,
          n.y,
          0,
          n.x,
          n.y,
          n.radius,
        )
        coreGrad.addColorStop(0, '#ffffff')
        coreGrad.addColorStop(0.4, n.color)
        coreGrad.addColorStop(1, n.type === 'agent' ? '#9D4EDD' : '#4A90E2')
        ctx.beginPath()
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2)
        ctx.fillStyle = coreGrad
        ctx.fill()
      })

      animFrame = requestAnimationFrame(draw)
    }

    // Only animate while the hero is on screen — keeps weak/ embedded
    // devices responsive instead of running two rAF loops forever.
    let running = false
    const start = () => {
      if (!running) {
        running = true
        animFrame = requestAnimationFrame(draw)
      }
    }
    const stop = () => {
      running = false
      cancelAnimationFrame(animFrame)
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (reduced.matches) {
      draw()
      cancelAnimationFrame(animFrame)
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => (e.isIntersecting ? start() : stop()))
        },
        { threshold: 0.05 },
      )
      io.observe(canvas)
      const onVis = () => (document.hidden ? stop() : start())
      document.addEventListener('visibilitychange', onVis)
      return () => {
        stop()
        io.disconnect()
        document.removeEventListener('visibilitychange', onVis)
        window.removeEventListener('resize', resize)
      }
    }

    return () => {
      cancelAnimationFrame(animFrame)
      window.removeEventListener('resize', resize)
    }
  }, [])

  const stats = [
    { val: '3', label: 'Products, One Governance Kernel' },
    { val: '6+', label: 'Database Connectors' },
    { val: '25+', label: 'PII Entity Types Detected' },
    { val: '20+', label: 'Years of Data Experience' },
  ]

  return (
    <section
      id="hero"
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background:
          'linear-gradient(160deg, #0A0F1F 0%, #0d1428 50%, #0A0F1F 100%)',
      }}
    >
      {/* Animated canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0.8,
        }}
      />

      {/* Radial vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 70% 70% at 50% 50%, transparent 20%, #0A0F1F 90%)',
          pointerEvents: 'none',
        }}
      />

      {/* Gradient lines */}
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '-10%',
          width: '50%',
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(74, 144, 226, 0.4), transparent)',
          transform: 'rotate(-15deg)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '25%',
          right: '-10%',
          width: '40%',
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(45, 212, 191, 0.35), transparent)',
          transform: 'rotate(-15deg)',
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 2rem',
          textAlign: 'center',
          paddingTop: '100px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 16px',
              borderRadius: '100px',
              background: 'rgba(74, 144, 226, 0.1)',
              border: '1px solid rgba(74, 144, 226, 0.3)',
              marginBottom: '2rem',
              maxWidth: '100%',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#4A90E2',
                animation: 'heroPulse 2s ease-in-out infinite',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(0.62rem, 2.4vw, 0.8rem)',
                fontWeight: 500,
                color: '#4A90E2',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                lineHeight: 1.5,
              }}
            >
              The Agentic Data Engineering Company
            </span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          style={{
            fontFamily: fontSans,
            fontSize: 'clamp(2.8rem, 7vw, 5.5rem)',
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            color: '#ffffff',
            margin: '0 auto 1.5rem',
            maxWidth: '960px',
          }}
        >
          Orchestrate{' '}
          <span
            style={{
              background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Intelligence.
          </span>
          <br />
          Not Just Pipelines.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          style={{
            fontFamily: fontSans,
            fontSize: 'clamp(1rem, 2vw, 1.2rem)',
            fontWeight: 400,
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.6)',
            maxWidth: '720px',
            margin: '0 auto 2.5rem',
          }}
        >
          SynKrasis is a family of products built on one governance kernel: an
          agentic data platform for ingestion, cataloging and quality;{' '}
          <span style={{ color: 'rgba(255,255,255,0.85)' }}>Ontext</span>, the
          governed ontology layer that lets AI agents query your data safely;
          and <span style={{ color: 'rgba(255,255,255,0.85)' }}>Sentinel</span>,
          free PII compliance for the AI era.
        </motion.p>

        {/* Product chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
            marginBottom: '2.75rem',
          }}
        >
          {products.map((p) => (
            <a
              key={p.id}
              href={`#${p.id}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '9px',
                padding: '9px 18px',
                borderRadius: '100px',
                background: hexToRgba(p.accent, 0.07),
                border: `1px solid ${hexToRgba(p.accent, 0.28)}`,
                fontFamily: fontSans,
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                transition: 'all 0.25s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = hexToRgba(p.accent, 0.14)
                e.currentTarget.style.borderColor = hexToRgba(p.accent, 0.55)
                e.currentTarget.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = hexToRgba(p.accent, 0.07)
                e.currentTarget.style.borderColor = hexToRgba(p.accent, 0.28)
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: p.accent,
                  boxShadow: `0 0 10px ${hexToRgba(p.accent, 0.9)}`,
                }}
              />
              {p.name}
            </a>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <a
            href="#demo"
            style={{
              fontFamily: fontSans,
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#ffffff',
              textDecoration: 'none',
              padding: '0.875rem 2rem',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
              boxShadow: '0 0 40px rgba(74, 144, 226, 0.4)',
              transition: 'all 0.25s ease',
              display: 'inline-block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow =
                '0 8px 40px rgba(74, 144, 226, 0.6)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow =
                '0 0 40px rgba(74, 144, 226, 0.4)'
            }}
          >
            Get a Demo
          </a>

          <a
            href="#products"
            style={{
              fontFamily: fontSans,
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'rgba(255,255,255,0.85)',
              textDecoration: 'none',
              padding: '0.875rem 1.75rem',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(13, 20, 40, 0.55)',
              transition: 'all 0.25s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
              e.currentTarget.style.background = 'rgba(74, 144, 226, 0.08)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle
                cx="8"
                cy="8"
                r="7"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path d="M6.5 5.5L11 8L6.5 10.5V5.5Z" fill="currentColor" />
            </svg>
            Explore the Products
          </a>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          style={{
            marginTop: '5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '3rem',
            flexWrap: 'wrap',
            paddingBottom: '2rem',
          }}
        >
          {stats.map((stat) => (
            <div key={stat.label} style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: fontSans,
                  fontSize: '2rem',
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {stat.val}
              </div>
              <div
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.45)',
                  marginTop: '2px',
                  letterSpacing: '0.04em',
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      <style>{`
        @keyframes heroPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </section>
  )
}
